import {
  Alert,
  BOOLEAN_SELECT_LIST_ITEMS,
  BULK_EDIT_UPDATE_EMPTY_WARNING,
  Card,
  Checkbox,
  Grid,
  pn,
  Stack,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Input, Select } from "components";
import {
  LkEndReasonTypeId,
  LkStudentEnrollmentDisplayFieldId,
  StudentEnrollment,
  StudentEnrollmentBulkUpdate,
  StudentEnrollmentBulkUpdateResource,
  StudentEnrollmentBulkUpdateSchema,
} from "generated/openApi";
import produce from "immer";

export const EnrollmentUpdateSchema = (props: {
  updateSchema: StudentEnrollmentBulkUpdateSchema;
  setUpdateSchema: (value: StudentEnrollmentBulkUpdateSchema) => void;
  resource: StudentEnrollmentBulkUpdateResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties =
    props.resource.properties?.find((x) => x.propertyName === pn<StudentEnrollmentBulkUpdate>("updateSchema"))
      ?.children || [];
  const displayFields = props.resource.displayFields || [];
  const startDateDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.StartDate);
  const schoolDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.School);
  const collegeDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.College);
  const advisorDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Advisor);
  const diplomaTypeDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.DiplomaType);

  const graduatedEndReasons = props.resource.endReasons?.filter(
    (x) => x.endReasonTypeId == LkEndReasonTypeId.Graduated || x.endReasonTypeId == LkEndReasonTypeId.GraduatedWithRigorous || x.endReasonTypeId == LkEndReasonTypeId.GraduatedWithoutRigorous
  );

  const enrollmentStatusDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.EnrollmentStatus
  );
  const creditsDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Credits);
  const dependentStatusDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.DependentStatus
  );
  const gradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.GradeLevel);
  const nihDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Nih);
  const agiDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Agi);
  const unmetNeedDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.UnmetNeed);
  const loanAmountDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.LoanAmount);
  const pellRecipientDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.PellRecipient);
  const academicStandingDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.AcademicStanding
  );
  const lepInstructionDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.LEPInstruction);
  const participantLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.ParticipantLevel);
  const ccpDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Ccp);
  const rigorousCDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.RigorousC);
  const dualEnrollmentDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.DualEnrollment
  );

  const dualEnrollmentParticipationDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.DualEnrollmentParticipation);
  const dualEnrollmentCredentialsDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.DualEnrollmentCredentials);
  const workStudyDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.WorkStudy);
  const employmentUbDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.EmploymentUB);
  const culturalDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Cultural);
  const communityServiceDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.CommunityService);

  const pellRecipientField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("pellRecipient");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updatePellRecipient;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updatePellRecipient: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={pellRecipientDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.pellRecipient)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.pellRecipient = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const loanAmountField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("loanAmount");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateLoanAmount;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateLoanAmount: e.target.checked })}
        />
        <Input
          modelProperty={property}
          label={loanAmountDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.loanAmount}
          onChange={(e) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.loanAmount = e.target.value as unknown as number;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const unmetNeedField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("unmetNeed");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateUnmetNeed;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateUnmetNeed: e.target.checked })}
        />
        <Input
          modelProperty={property}
          label={unmetNeedDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.unmetNeed}
          onChange={(e) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.unmetNeed = e.target.value as unknown as number;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const agiField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("agi");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAgi;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAgi: e.target.checked })}
        />
        <Input
          modelProperty={property}
          label={agiDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.agi}
          onChange={(e) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.agi = e.target.value as unknown as number;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const nihField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("nih");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateNih;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateNih: e.target.checked })}
        />
        <Input
          modelProperty={property}
          label={nihDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.nih}
          onChange={(e) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.nih = e.target.value as unknown as number;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const creditsField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("credits");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateCredits;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateCredits: e.target.checked })}
        />
        <Input
          modelProperty={property}
          label={creditsDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.credits}
          onChange={(e) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.credits = e.target.value as unknown as number;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const dependentStatusField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("dependentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDependentStatusId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDependentStatusId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={dependentStatusDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.dependentStatuses?.find((x) => x.id === props.updateSchema.dependentStatusId)}
          options={props.resource.dependentStatuses}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.dependentStatusId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const academicStandingField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("academicStandingId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAcademicStandingId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAcademicStandingId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={academicStandingDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.academicStandings?.find((x) => x.id === props.updateSchema.academicStandingId)}
          options={props.resource.academicStandings}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.academicStandingId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const enrollmentStatusField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("enrollmentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEnrollmentStatusId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEnrollmentStatusId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={enrollmentStatusDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.enrollmentStatuses?.find((x) => x.id === props.updateSchema.enrollmentStatusId)}
          options={props.resource.enrollmentStatuses}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.enrollmentStatusId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const yearField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("year");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateYear;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateYear: e.target.checked })}
        />
        <Select
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.resource.schoolYears?.find((x) => x.id === props.updateSchema.year)}
          options={props.resource.schoolYears}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.year = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateSchoolId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateSchoolId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={schoolDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.schools?.find((x) => x.id === props.updateSchema.schoolId)}
          options={props.resource.schools}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.schoolId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
          required={isUpdating}
        />
      </Stack>
    );
  };

  const gradeLevelField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("gradeLevelId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateGradeLevelId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateGradeLevelId: e.target.checked })}
        />
        <Select
          label={gradeLevelDisplayField?.displayName}
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.resource.gradeLevels?.find((x) => x.id === props.updateSchema.gradeLevelId)}
          options={props.resource.gradeLevels}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.gradeLevelId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const startDateField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("startDate");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateStartDate;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateStartDate: e.target.checked })}
        />
        <DatePicker
          modelProperty={property}
          label={startDateDisplayField?.displayName}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.startDate}
          onChange={(value) => props.setUpdateSchema({ ...props.updateSchema, startDate: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const endDateField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("endDate");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEndDate;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEndDate: e.target.checked })}
        />
        <DatePicker
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.endDate}
          onChange={(value) => props.setUpdateSchema({ ...props.updateSchema, endDate: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const endReasonField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("endReasonId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEndReasonId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEndReasonId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.endReasons?.find((x) => x.id === props.updateSchema.endReasonId)}
          options={props.resource.endReasons}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.endReasonId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
          required={isUpdating}
        />
      </Stack>
    );
  };

  const diplomaTypeField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("diplomaTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDiplomaTypeId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDiplomaTypeId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.diplomaTypes?.find((x) => x.id === props.updateSchema.diplomaTypeId)}
          options={props.resource.diplomaTypes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.diplomaTypeId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
          required={isUpdating}
        />
      </Stack>
    );
  };

  const advisorField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("advisorId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAdvisorId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAdvisorId: e.target.checked })}
        />
        <Select
          label={advisorDisplayField?.displayName}
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.advisors?.find((x) => x.id === props.updateSchema.advisorId)}
          options={props.resource.advisors}
          getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.advisorId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
          required={isUpdating}
        />
      </Stack>
    );
  };

  const iepField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("iepId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateIepId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateIepId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.ieps?.find((x) => x.id === props.updateSchema.iepId)}
          options={props.resource.ieps}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, iepId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const lepField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("lepId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateLepId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateLepId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.leps?.find((x) => x.id === props.updateSchema.lepId)}
          options={props.resource.leps}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, lepId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const lepInstructionField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("lepInstructionId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateLepInstructionId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateLepInstructionId: e.target.checked })}
        />
        <Select
          label={lepInstructionDisplayField?.displayName}
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.leps?.find((x) => x.id === props.updateSchema.lepInstructionId)}
          options={props.resource.leps}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, lepInstructionId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const initialEnrollmentStatusField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("initialEnrollmentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateInitialEnrollmentStatusId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, updateInitialEnrollmentStatusId: e.target.checked })
          }
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.enrollmentStatuses?.find((x) => x.id === props.updateSchema.initialEnrollmentStatusId)}
          options={props.resource.enrollmentStatuses}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.initialEnrollmentStatusId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const finalEnrollmentStatusField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("finalEnrollmentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateFinalEnrollmentStatusId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, updateFinalEnrollmentStatusId: e.target.checked })
          }
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.enrollmentStatuses?.find((x) => x.id === props.updateSchema.finalEnrollmentStatusId)}
          options={props.resource.enrollmentStatuses}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.finalEnrollmentStatusId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const participantStatusField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("participantStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateParticipantStatusId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, updateParticipantStatusId: e.target.checked })
          }
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.participantStatuses?.find((x) => x.id === props.updateSchema.participantStatusId)}
          options={props.resource.participantStatuses}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.participantStatusId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const participantLevelField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("participantLevelId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateParticipantLevelId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, updateParticipantLevelId: e.target.checked })
          }
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.participantLevels?.find((x) => x.id === props.updateSchema.participantLevelId)}
          options={props.resource.participantLevels}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.participantLevelId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const militaryConnectionField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("militaryConnectionId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateMilitaryConnectionId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, updateMilitaryConnectionId: e.target.checked })
          }
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.militaryConnections?.find((x) => x.id === props.updateSchema.militaryConnectionId)}
          options={props.resource.militaryConnections}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.militaryConnectionId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const persistantStatusField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("persistantStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updatePersistantStatusId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updatePersistantStatusId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.resource.persistantStatuses?.find((x) => x.id === props.updateSchema.persistantStatusId)}
          options={props.resource.persistantStatuses}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.persistantStatusId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const freeReducedLunchField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("freeReducedLunch");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateFreeReducedLunch;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateFreeReducedLunch: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.freeReducedLunch)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.freeReducedLunch = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const magnetField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("magnet");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateMagnet;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateMagnet: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.magnet)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.magnet = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const heldBackField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("heldBack");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateHeldBack;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateHeldBack: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.heldBack)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.heldBack = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const twentyFirstCenturyField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("twentyFirstCentury");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateTwentyFirstCentury;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateTwentyFirstCentury: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.twentyFirstCentury)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, twentyFirstCentury: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const avidField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("avid");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAvid;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAvid: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.avid)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, avid: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const mediaReleaseField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("mediaRelease");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateMediaRelease;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateMediaRelease: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.mediaRelease)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.mediaRelease = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const cacgField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("cacg");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateCacg;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateCacg: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.cacg)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.cacg = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const dropTransferFormField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("dropTransferForm");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDropTransferForm;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDropTransferForm: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.dropTransferForm)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.dropTransferForm = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const homelessField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("homeless");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateHomeless;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateHomeless: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.homeless)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.homeless = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const fosterCareField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("fosterCare");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateFosterCare;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateFosterCare: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.fosterCare)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.fosterCare = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const atRiskField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("atRisk");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAtRisk;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAtRisk: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.atRisk)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.atRisk = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const ubField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("ub");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateUb;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateUb: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.ub)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.ub = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const ubmsField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("ubms");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateUbms;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateUbms: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.ubms)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.ubms = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const vubField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("vub");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateVub;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateVub: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.vub)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.vub = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const tsField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("ts");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateTs;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateTs: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.ts)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.ts = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const sssField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("sss");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateSss;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateSss: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.sss)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.sss = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const gearUpField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("gearUp");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateGearUp;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateGearUp: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.gearUp)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.gearUp = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const eocField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("eoc");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEoc;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEoc: e.target.checked })}
        />
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.eoc)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.eoc = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const ccpField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("ccpId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateCcpId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateCcpId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={ccpDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.ccps?.find((x) => x.id === props.updateSchema.ccpId)}
          options={props.resource.ccps}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.ccpId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const rigorousCField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("rigorousCId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateRigorousCId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateRigorousCId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={rigorousCDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.rigorousCs?.find((x) => x.id === props.updateSchema.rigorousCId)}
          options={props.resource.rigorousCs}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.rigorousCId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const dualEnrollmentField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("dualEnrollment");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDualEnrollment;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDualEnrollment: e.target.checked })}
        />
        <Select
          label={dualEnrollmentDisplayField?.displayName}
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.dualEnrollment)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.dualEnrollment = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const dualEnrollmentParticipationField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("dualEnrollmentParticipationId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDualEnrollmentParticipation;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDualEnrollmentParticipation: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={dualEnrollmentParticipationDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.dualEnrollmentParticipations?.find((x) => x.id === props.updateSchema.dualEnrollmentParticipationId)}
          options={props.resource.dualEnrollmentParticipations}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.dualEnrollmentParticipationId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const dualEnrollmentCredentialField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("dualEnrollmentCredentialsId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDualEnrollmentCredentials;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDualEnrollmentCredentials: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={dualEnrollmentCredentialsDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.dualEnrollmentCredentials?.find((x) => x.id === props.updateSchema.dualEnrollmentCredentialsId)}
          options={props.resource.dualEnrollmentCredentials}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.dualEnrollmentCredentialsId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const employmentUbField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("employmentUBId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEmploymentUb;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEmploymentUb: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={employmentUbDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.employmentUbs?.find((x) => x.id === props.updateSchema.employmentUBId)}
          options={props.resource.employmentUbs}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.employmentUBId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const workStudyField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("workStudyId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateWorkStudy;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateWorkStudy: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={workStudyDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.workStudyCulturalCommunities?.find((x) => x.id === props.updateSchema.workStudyId)}
          options={props.resource.workStudyCulturalCommunities}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.workStudyId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const culturalField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("culturalId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateCultural;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateCultural: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={culturalDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.workStudyCulturalCommunities?.find((x) => x.id === props.updateSchema.culturalId)}
          options={props.resource.workStudyCulturalCommunities}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.culturalId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const communityServiceField = () => {
    const propertyName = pn<StudentEnrollmentBulkUpdateSchema>("communityServiceId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateCommunityService;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateCommunityService: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={communityServiceDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.workStudyCulturalCommunities?.find((x) => x.id === props.updateSchema.communityServiceId)}
          options={props.resource.workStudyCulturalCommunities}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema(
              produce(props.updateSchema, (draft) => {
                draft.communityServiceId = value?.id;
              })
            )
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  return (
    <Card header="Updating Fields">
      <Stack spacing="md">
        <Alert severity="warning" variant="standard">
          {BULK_EDIT_UPDATE_EMPTY_WARNING}
        </Alert>

        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {yearField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {schoolField()}
          </Grid>

          {gradeLevelDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {gradeLevelField()}
            </Grid>
          )}

          {enrollmentStatusDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {enrollmentStatusField()}
            </Grid>
          )}

          {creditsDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {creditsField()}
            </Grid>
          )}

          {advisorDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {advisorField()}
            </Grid>
          )}

          <Grid item xs={smallFieldBreakpoints}>
            {startDateField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {endDateField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {endReasonField()}
          </Grid>

          {(diplomaTypeDisplayField && graduatedEndReasons?.some(e => e.id == props.updateSchema.endReasonId)) && (
            <Grid item xs={smallFieldBreakpoints}>
              {diplomaTypeField()}
            </Grid>
          )}

          {academicStandingDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {academicStandingField()}
            </Grid>
          )}

          {props.resource.variables?.iep && (
            <Grid item xs={smallFieldBreakpoints}>
              {iepField()}
            </Grid>
          )}

          {props.resource.variables?.lep && (
            <Grid item xs={smallFieldBreakpoints}>
              {lepField()}
            </Grid>
          )}

          {props.resource.variables?.initialEnrollmentStatusId && (
            <Grid item xs={smallFieldBreakpoints}>
              {initialEnrollmentStatusField()}
            </Grid>
          )}

          {props.resource.variables?.finalEnrollmentStatusId && (
            <Grid item xs={smallFieldBreakpoints}>
              {finalEnrollmentStatusField()}
            </Grid>
          )}

          {props.resource.variables?.participantStatusId && (
            <Grid item xs={smallFieldBreakpoints}>
              {participantStatusField()}
            </Grid>
          )}

          {participantLevelDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {participantLevelField()}
            </Grid>
          )}

          {dependentStatusDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dependentStatusField()}
            </Grid>
          )}

          {nihDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {nihField()}
            </Grid>
          )}

          {agiDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {agiField()}
            </Grid>
          )}

          {unmetNeedDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {unmetNeedField()}
            </Grid>
          )}

          {loanAmountDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {loanAmountField()}
            </Grid>
          )}

          {lepInstructionDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {lepInstructionField()}
            </Grid>
          )}

          {pellRecipientDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {pellRecipientField()}
            </Grid>
          )}

          {props.resource.variables?.persistantStatusId && (
            <Grid item xs={smallFieldBreakpoints}>
              {persistantStatusField()}
            </Grid>
          )}

          {props.resource.variables?.militaryConnectionId && (
            <Grid item xs={smallFieldBreakpoints}>
              {militaryConnectionField()}
            </Grid>
          )}

          {props.resource.variables?.freeReducedLunch && (
            <Grid item xs={smallFieldBreakpoints}>
              {freeReducedLunchField()}
            </Grid>
          )}

          {props.resource.variables?.magnet && (
            <Grid item xs={smallFieldBreakpoints}>
              {magnetField()}
            </Grid>
          )}

          {props.resource.variables?.heldBack && (
            <Grid item xs={smallFieldBreakpoints}>
              {heldBackField()}
            </Grid>
          )}

          {props.resource.variables?.twentyFirstCentury && (
            <Grid item xs={smallFieldBreakpoints}>
              {twentyFirstCenturyField()}
            </Grid>
          )}

          {props.resource.variables?.avid && (
            <Grid item xs={smallFieldBreakpoints}>
              {avidField()}
            </Grid>
          )}

          {props.resource.variables?.mediaRelease && (
            <Grid item xs={smallFieldBreakpoints}>
              {mediaReleaseField()}
            </Grid>
          )}

          {props.resource.variables?.cacg && (
            <Grid item xs={smallFieldBreakpoints}>
              {cacgField()}
            </Grid>
          )}

          {props.resource.variables?.dropTransferForm && (
            <Grid item xs={smallFieldBreakpoints}>
              {dropTransferFormField()}
            </Grid>
          )}

          {props.resource.variables?.homeless && (
            <Grid item xs={smallFieldBreakpoints}>
              {homelessField()}
            </Grid>
          )}

          {props.resource.variables?.fosterCare && (
            <Grid item xs={smallFieldBreakpoints}>
              {fosterCareField()}
            </Grid>
          )}

          {props.resource.variables?.atRisk && (
            <Grid item xs={smallFieldBreakpoints}>
              {atRiskField()}
            </Grid>
          )}

          {props.resource.variables?.ub && (
            <Grid item xs={smallFieldBreakpoints}>
              {ubField()}
            </Grid>
          )}

          {props.resource.variables?.ubms && (
            <Grid item xs={smallFieldBreakpoints}>
              {ubmsField()}
            </Grid>
          )}

          {props.resource.variables?.vub && (
            <Grid item xs={smallFieldBreakpoints}>
              {vubField()}
            </Grid>
          )}

          {props.resource.variables?.ts && (
            <Grid item xs={smallFieldBreakpoints}>
              {tsField()}
            </Grid>
          )}

          {props.resource.variables?.sss && (
            <Grid item xs={smallFieldBreakpoints}>
              {sssField()}
            </Grid>
          )}

          {props.resource.variables?.gearUp && (
            <Grid item xs={smallFieldBreakpoints}>
              {gearUpField()}
            </Grid>
          )}

          {rigorousCDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {rigorousCField()}
            </Grid>
          )}

          {ccpDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {ccpField()}
            </Grid>
          )}

          {props.resource.variables?.eoc && (
            <Grid item xs={smallFieldBreakpoints}>
              {eocField()}
            </Grid>
          )}

          {dualEnrollmentDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dualEnrollmentField()}
            </Grid>
          )}

          {dualEnrollmentParticipationDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dualEnrollmentParticipationField()}
            </Grid>
          )}

          {dualEnrollmentCredentialsDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dualEnrollmentCredentialField()}
            </Grid>
          )}

          {employmentUbDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {employmentUbField()}
            </Grid>
          )}

          {workStudyDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {workStudyField()}
            </Grid>
          )}

          {culturalDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {culturalField()}
            </Grid>
          )}

          {communityServiceDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {communityServiceField()}
            </Grid>
          )}
        </Grid>
      </Stack>
    </Card>
  );
};
